import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { LOCAL_STORAGE } from './injection-tokens';

export type GrooveCandidate = 'none' | 'warm' | 'neutral' | 'cool';
const STORAGE_KEY = 'grooveCandidate';
const ENABLED_KEY = 'grooveSwitcherEnabled';
const CANDIDATE_CLASS: Record<Exclude<GrooveCandidate, 'none'>, string> = {
  warm: 'cand-warm',
  neutral: 'cand-neutral',
  cool: 'cand-cool',
};

/**
 * DEV-ONLY. Applies a candidate neutral-palette class to <html> so the whole
 * app (chrome + content) re-themes for the Groove undertone decision. Temporary
 * scaffolding — remove with the candidate ramps once the undertone is chosen.
 */
@Injectable({ providedIn: 'root' })
export class GrooveCandidateService {
  private readonly document = inject(DOCUMENT);
  private readonly localStorage = inject(LOCAL_STORAGE);
  private readonly platformId = inject(PLATFORM_ID);

  readonly candidate = signal<GrooveCandidate>(this.getInitialValue());
  readonly enabled = signal<boolean>(this.getEnabled());

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.apply(this.candidate());
      this.document.addEventListener('keydown', (event) => {
        if (
          event.ctrlKey &&
          event.altKey &&
          (event.key === 'g' || event.key === 'G')
        ) {
          event.preventDefault();
          this.toggleEnabled();
        }
      });
    }
  }

  set(candidate: GrooveCandidate): void {
    this.candidate.set(candidate);
    this.localStorage?.setItem(STORAGE_KEY, candidate);
    this.apply(candidate);
  }

  toggleEnabled(): void {
    const next = !this.enabled();
    this.enabled.set(next);
    this.localStorage?.setItem(ENABLED_KEY, next ? '1' : '0');
  }

  private apply(candidate: GrooveCandidate): void {
    const classList = this.document.documentElement.classList;
    Object.values(CANDIDATE_CLASS).forEach((cls) => {
      classList.remove(cls);
    });
    if (candidate !== 'none') {
      classList.add(CANDIDATE_CLASS[candidate]);
    }
  }

  private getInitialValue(): GrooveCandidate {
    const stored = this.localStorage?.getItem(
      STORAGE_KEY,
    ) as GrooveCandidate | null;
    return stored ?? 'none';
  }

  private getEnabled(): boolean {
    return this.localStorage?.getItem(ENABLED_KEY) === '1';
  }
}
